import { Wind, Scissors, Sprout, ArrowUpRight } from "lucide-react";

const services = [
  {
    id: "mowing",
    title: "Mowing",
    blurb:
      "Weekly or bi‑weekly cuts at the right height for your turf. We change direction every visit and lay down clean ballpark stripes that make the neighbors look twice.",
    bullets: ["Custom stripe patterns", "Sharp blades every cut", "Clippings cleaned up"],
    icon: Wind,
    span: "lg:col-span-7 lg:row-span-2",
    tone: "dark",
  },
  {
    id: "trimming",
    title: "Trimming & Edging",
    blurb:
      "Crisp borders along driveways, walkways and beds. Trim work that finishes the lawn the way it deserves.",
    bullets: ["String trim every visit", "Bed & sidewalk edging"],
    icon: Scissors,
    span: "lg:col-span-5",
    tone: "light",
  },
  {
    id: "fert",
    title: "Fertilization",
    blurb:
      "Seasonal feeding programs to keep your lawn deep‑green, thick, and weed‑resistant from spring through fall.",
    bullets: ["Granular & spot treatments", "Northern‑MI tuned schedule"],
    icon: Sprout,
    span: "lg:col-span-5",
    tone: "orange",
  },
];

function Card({ s }) {
  const Icon = s.icon;
  const isDark = s.tone === "dark";
  const isOrange = s.tone === "orange";
  return (
    <article
      data-testid={`service-card-${s.id}`}
      className={`hover-lift-craftsman group relative border-2 border-[#0B1C14] p-7 sm:p-9 flex flex-col justify-between ${
        isDark
          ? "bg-[#0B1C14] text-[#F4F3EF]"
          : isOrange
          ? "bg-[#E64C23] text-white"
          : "bg-white text-[#0B1C14]"
      } ${s.span}`}
    >
      <div>
        <div className="flex items-start justify-between">
          <div
            className={`w-14 h-14 flex items-center justify-center border-2 ${
              isDark
                ? "border-[#F4F3EF]/30 bg-[#0F2418]"
                : isOrange
                ? "border-white/40 bg-white/10"
                : "border-[#0B1C14] bg-[#F4F3EF]"
            }`}
          >
            <Icon
              size={26}
              strokeWidth={2}
              className={isDark || isOrange ? "text-white" : "text-[#0B1C14]"}
            />
          </div>
          <span
            className={`font-mono-accent text-[10px] tracking-[0.22em] uppercase ${
              isDark || isOrange ? "text-white/70" : "text-[#4A5D53]"
            }`}
          >
            Service /
            {s.id === "mowing" ? " 01" : s.id === "trimming" ? " 02" : " 03"}
          </span>
        </div>

        <h3
          className={`font-display uppercase tracking-tight mt-8 ${
            s.id === "mowing"
              ? "text-4xl sm:text-5xl lg:text-6xl"
              : "text-3xl sm:text-4xl"
          }`}
        >
          {s.title}
        </h3>

        <p
          className={`mt-5 max-w-md text-sm sm:text-base leading-relaxed ${
            isDark
              ? "text-[#F4F3EF]/75"
              : isOrange
              ? "text-white/90"
              : "text-[#4A5D53]"
          }`}
        >
          {s.blurb}
        </p>

        <ul className="mt-6 space-y-2">
          {s.bullets.map((b) => (
            <li
              key={b}
              className={`flex items-start gap-3 text-sm font-semibold ${
                isDark
                  ? "text-[#F4F3EF]"
                  : isOrange
                  ? "text-white"
                  : "text-[#0B1C14]"
              }`}
            >
              <span
                className={`mt-[7px] w-3 h-[2px] ${
                  isDark
                    ? "bg-[#E64C23]"
                    : isOrange
                    ? "bg-white"
                    : "bg-[#E64C23]"
                }`}
              />
              {b}
            </li>
          ))}
        </ul>
      </div>

      <a
        href="#contact"
        data-testid={`service-cta-${s.id}`}
        className={`mt-8 inline-flex items-center gap-2 font-mono-accent text-[11px] tracking-[0.22em] uppercase self-start border-b-2 pb-1 transition-colors ${
          isDark
            ? "border-[#E64C23] text-[#E64C23] hover:text-white hover:border-white"
            : isOrange
            ? "border-white text-white"
            : "border-[#0B1C14] text-[#0B1C14] hover:border-[#E64C23] hover:text-[#E64C23]"
        }`}
      >
        Get this service
        <ArrowUpRight size={14} strokeWidth={2.5} />
      </a>
    </article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 lg:py-32 bg-[#F4F3EF]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-6 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="font-mono-accent text-[11px] tracking-[0.25em] uppercase text-[#E64C23] mb-5">
              / Services
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0B1C14] leading-[0.95]">
              Three things,
              <br />
              done <span className="italic">properly.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-[#4A5D53] text-base sm:text-lg leading-relaxed">
            No upsells. No fluff. We keep our list short on purpose so every
            blade gets the attention it should. Pick a service or bundle the
            three for a full‑season program.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 lg:auto-rows-[minmax(0,1fr)] gap-6">
          {services.map((s) => (
            <Card key={s.id} s={s} />
          ))}
        </div>
      </div>
    </section>
  );
}
